import type { ReactNode } from "react";
import { Header } from "./Header";
import { Sidebar } from "./Sidebar";

export interface AppShellProps {
  /**
   * Header slot. Pass a fully-composed node to override; omit to fall
   * back to the default `<Header />` (an empty bar). Pass `false` (or
   * `null`) to suppress the region entirely.
   */
  header?: ReactNode;
  /**
   * Sidebar slot. Same fallback semantics as `header`. Omit → default
   * empty `<Sidebar />`; `null`/`false` → no rail (content goes
   * full-width).
   */
  sidebar?: ReactNode;
  /**
   * Footer slot. v35 has no footer, so the default is NO footer — only
   * renders when a node is explicitly passed.
   */
  footer?: ReactNode;
  /** Main content (the routed page). */
  children: ReactNode;
  /** Optional extra classes for the outermost shell element. */
  className?: string;
}

/**
 * AppShell — the **default** slot-based composition (NOT a `<Layout>`
 * monolith).
 *
 * Mirrors v35's `.app-shell` full-page frame: a column of
 * `header → body → (optional footer)`, where `body` is a row of
 * `sidebar + main`, `main` scrolls independently, and the shell never
 * exceeds the viewport (`h-screen overflow-hidden`).
 *
 * This is a sensible default each surface composes its OWN shell on
 * top of (web composes its own Header/Sidebar/Footer, or falls back to
 * these). It owns the frame geometry only; what goes IN each slot is
 * the surface's concern. Per D6, shared `ui` provides the default
 * composition, not the layout — surfaces diverge freely.
 *
 * Slot fallback rules:
 *   - `undefined` → render the matching default primitive (empty
 *     `Header`/`Sidebar`; footer has no default and is omitted)
 *   - `null` / `false` → suppress that region (full-width content, no
 *     rail/bar)
 *   - any node → render it as-is
 *
 * Tokenized: `bg-bg` + Tailwind layout utilities only — zero arbitrary
 * values, zero inline styles.
 */
export function AppShell({
  header,
  sidebar,
  footer,
  children,
  className,
}: AppShellProps) {
  const headerNode =
    header === undefined ? <Header /> : header === false ? null : header;
  const sidebarNode =
    sidebar === undefined ? (
      <Sidebar>{null}</Sidebar>
    ) : sidebar === false ? null : (
      sidebar
    );
  // Footer has no default (v35 has none): render the passed node as-is,
  // suppress on undefined/null/false. Surfaces that want a footer pass a
  // composed `<Footer>…</Footer>` (the primitive) into this slot.
  const footerNode =
    footer == null || footer === false ? null : footer;

  const shell = "flex flex-col h-screen overflow-hidden bg-bg text-text";

  return (
    <div
      data-testid="app-shell"
      className={[shell, className].filter(Boolean).join(" ")}
    >
      {headerNode}
      <div className="flex flex-1 overflow-hidden">
        {sidebarNode}
        {/* `relative` IS THE PAGE-HEIGHT FIX, not a styling preference.
            Jared, 2026-08-25, on the Search Dashboard: *"why is this page so
            long? Basically, all this gray area. I guess the window should be
            the same height as this thing."* Measured on 2026-08-27 at 1280x800:
            `documentElement.scrollHeight` was 1623 against an 800px viewport,
            so the WINDOW had its own scrollbar into empty space below a footer
            that is pinned by `h-screen overflow-hidden` above.

            The cause is `.sr-only`, which is `position: absolute` with no
            offsets. Every chart's keyboard live region wears it
            (`additions-readout`, `outreach-readout`, the two `stage-readout`s),
            and until this line NOTHING between them and `<html>` was
            positioned — so their containing block was the initial one. An
            `overflow: hidden` ancestor does not clip an absolutely positioned
            descendant whose containing block sits ABOVE the clipper, and such a
            box contributes to the VIEWPORT's scrollable overflow. The taller
            the page, the further down the last live region sat, and the more
            empty space the window scrolled into. It reproduced on both Search
            Detail tabs and on no other route, because no other route draws
            charts.

            Making `main` the containing block puts those boxes back inside the
            scroller that already owns the page's scrolling. Verified live: the
            document collapses to exactly the viewport (1623 -> 800) while
            `main.scrollHeight` is byte-for-byte unchanged at 2479 — the fix
            SIZES the page rather than hiding content, which `overflow: hidden`
            on the document would not have. */}
        <main data-testid="app-main" className="relative flex-1 overflow-y-auto bg-bg">
          {children}
        </main>
      </div>
      {footerNode}
    </div>
  );
}
